import { useCallback, useEffect, useState } from 'react';

import { notifications } from '@mantine/notifications';

import { vnextApiClient } from '@/services/vnext-api';
import {
  RenderingContextCatalogue,
  ScopeComponent,
  ScopeComponentCreateRequest,
  ScopeComponentUpdateRequest,
} from '@/types';

export function useScopeComponents({ autoFetch = true, includeAll = false } = {}) {
  const [scopeComponents, setScopeComponents] = useState<ScopeComponent[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<Error | null>(null);

  const fetchScopeComponents = useCallback(async () => {
    try {
      setIsLoading(true);
      setError(null);
      const data = await vnextApiClient.getScopeComponents({ all: includeAll });
      setScopeComponents(data);
      return data;
    } catch (err) {
      const error = err instanceof Error ? err : new Error('Failed to fetch scope components');
      setError(error);
      throw error;
    } finally {
      setIsLoading(false);
    }
  }, []);

  const getComponent = useCallback(async (componentId: string): Promise<ScopeComponent> => {
    try {
      setIsLoading(true);
      setError(null);
      return await vnextApiClient.getScopeComponent(componentId);
    } catch (err) {
      const error = err instanceof Error ? err : new Error('Failed to fetch scope component');
      setError(error);
      throw error;
    } finally {
      setIsLoading(false);
    }
  }, []);

  const createComponent = useCallback(
    async (data: ScopeComponentCreateRequest): Promise<ScopeComponent> => {
      try {
        setIsLoading(true);
        setError(null);
        const created = await vnextApiClient.createScopeComponent(data);

        notifications.show({
          title: 'Success',
          message: `Component "${created.name}" has been created`,
          color: 'green',
        });

        await fetchScopeComponents();
        return created;
      } catch (err) {
        const error = err instanceof Error ? err : new Error('Failed to create scope component');
        setError(error);

        notifications.show({
          title: 'Error',
          message: error.message,
          color: 'red',
        });

        throw error;
      } finally {
        setIsLoading(false);
      }
    },
    [fetchScopeComponents]
  );

  const updateComponent = useCallback(
    async (componentId: string, data: ScopeComponentUpdateRequest): Promise<ScopeComponent> => {
      try {
        setIsLoading(true);
        setError(null);
        const updated = await vnextApiClient.updateScopeComponent(componentId, data);

        notifications.show({
          title: 'Success',
          message: 'Component updated successfully',
          color: 'green',
        });

        await fetchScopeComponents();
        return updated;
      } catch (err) {
        const error = err instanceof Error ? err : new Error('Failed to update scope component');
        setError(error);

        notifications.show({
          title: 'Error',
          message: error.message,
          color: 'red',
        });

        throw error;
      } finally {
        setIsLoading(false);
      }
    },
    [fetchScopeComponents]
  );

  const deleteComponent = useCallback(
    async (componentId: string): Promise<void> => {
      try {
        setIsLoading(true);
        setError(null);
        await vnextApiClient.deleteScopeComponent(componentId);

        notifications.show({
          title: 'Success',
          message: 'Component deleted successfully',
          color: 'green',
        });

        await fetchScopeComponents();
      } catch (err) {
        const error = err instanceof Error ? err : new Error('Failed to delete scope component');
        setError(error);

        notifications.show({
          title: 'Error',
          message: error.message,
          color: 'red',
        });

        throw error;
      } finally {
        setIsLoading(false);
      }
    },
    [fetchScopeComponents]
  );

  const getRenderingContext = useCallback(async (): Promise<RenderingContextCatalogue> => {
    try {
      return await vnextApiClient.getRenderingContext();
    } catch (err) {
      const error = err instanceof Error ? err : new Error('Failed to fetch rendering context');
      throw error;
    }
  }, []);

  useEffect(() => {
    if (autoFetch) {
      fetchScopeComponents();
    }
  }, [autoFetch, fetchScopeComponents]);

  return {
    scopeComponents,
    isLoading,
    error,
    fetchScopeComponents,
    getComponent,
    createComponent,
    updateComponent,
    deleteComponent,
    getRenderingContext,
  };
}
